// ** React Imports
import { useState } from 'react'

// ** Third Party Imports
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

// ** Component Imports
import { Button, Dialog, RadioButton } from '@/shared/ui'

// ** Styled Components
import {
	ButtonsColumn,
	ButtonsWrapper,
	Content,
	Description,
	PlusCircleIcon,
	StyledRadioGroup,
	StyledSpan,
	StyledTypography,
	Title
} from './styles'

// ** Entity Imports
import { useRefreshTokenMutation } from '@/entities/session/api/sessionApi'
import { useGetCurrentUserQuery } from '@/entities/user'

// ** Utility Imports
import {
	getFreePlanClientLimitMessage,
	hasAppAccess,
	isFreePlanClientLimitReached,
	isOnFreePlan
} from '@/lib/access'

// ** Feature Imports
import {
	EnterpriseModal,
	useGetClientCountQuery,
	useSkipActiveTrialMutation
} from '@/features/subscriptions'
import { ChoosePlanModal } from '@/pages/subscription-plan'

interface IProps {
	className?: string
}

const ENTERPRISE_THRESHOLD = 50
const REDIRECT_TIMEOUT_AMOUNT = 250

export const CreateNewWidget = ({ className }: IProps) => {
	// ** State
	const [open, setOpen] = useState(false)
	const [value, setValue] = useState('')
	const [isInfoModalOpen, setIsInfoModalOpen] = useState(false)
	const [isEnterpriseModalOpen, setIsEnterpriseModalOpen] = useState(false)
	const [isChoosePlanModalOpen, setIsChoosePlanModalOpen] = useState(false)

	// ** Hooks
	const navigate = useNavigate()

	// ** Queries
	const { data: currentUser, refetch: refetchCurrentUser } =
		useGetCurrentUserQuery()
	const { data: clientNumber } = useGetClientCountQuery(
		{ organizationId: currentUser?.organizationId },
		{
			// trigger only when the dialog is open
			skip: !open
		}
	)

	// ** Mutations
	const [skipActiveTrial, { isLoading }] = useSkipActiveTrialMutation()
	const [refresh] = useRefreshTokenMutation()

	const handleOpenChange = (open: boolean) => {
		setOpen(open)
		// refetch the current user when the dialog opens to get the latest data
		if (open) {
			refetchCurrentUser()
		}

		setValue('')
	}

	const handleSkipActiveTrial = async () => {
		try {
			await skipActiveTrial({}).unwrap()
			await refresh({}).unwrap()
			await refetchCurrentUser() // force a fresh fetch

			setOpen(false)
			setIsInfoModalOpen(false)

			toast.success('Successfully started Subscription!', {
				position: 'bottom-right'
			})

			setTimeout(() => {
				navigate('/client-management/create-client')
			}, REDIRECT_TIMEOUT_AMOUNT)
		} catch (err) {
			console.error(err)
			toast.error('Something went wrong.')
		}
	}

	const handleRedirect = () => {
		if (!hasAppAccess(currentUser)) {
			setIsChoosePlanModalOpen(true)
		} else if (
			value === '/client-management/create-client' &&
			isFreePlanClientLimitReached(currentUser)
		) {
			// the free plan's one client is taken — same upgrade prompt as above
			setIsChoosePlanModalOpen(true)
		} else if (
			value === '/client-management/create-client' &&
			currentUser?.freeTrail &&
			currentUser?.activeSubscription &&
			clientNumber?.currentClientNumber >= clientNumber?.maxClientNumber
		) {
			setIsInfoModalOpen(true)
		} else if (
			value === '/client-management/create-client' &&
			!currentUser?.freeTrail &&
			currentUser?.activeSubscription &&
			clientNumber?.currentClientNumber >= clientNumber?.maxClientNumber
		) {
			setIsInfoModalOpen(true)
		}
		// TODO: Re-enable when stripe implementation for enterprise is done
		// else if (
		// 	value === '/client-management/create-client' &&
		// 	currentUser?.activeSubscription &&
		// 	currentUser?.role?.name === 'Owner' &&
		// 	clientNumber?.currentClientNumber >= ENTERPRISE_THRESHOLD
		// ) {
		// 	setIsEnterpriseModalOpen(true)
		// }
		else {
			setOpen(false)
			navigate(value)
		}
	}

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<Dialog.Trigger>
				<Button
					label='Create New'
					className={className}
					startIcon={<PlusCircleIcon />}
				/>
			</Dialog.Trigger>
			<Content>
				<Dialog.Close />
				<Title variant='h2' weight='semi-bold'>
					Create New
				</Title>
				<Description variant='body2' weight='medium'>
					Please select the type of entity you want to create:
				</Description>
				<StyledRadioGroup value={value} onValueChange={setValue}>
					<RadioButton
						size='sm'
						value='/client-management/create-client'
						label='Client'
					/>
					<RadioButton
						size='sm'
						value='/client-management/create-company'
						label='Company'
					/>
					{/* <RadioButton
						size='sm'
						value='/client-dashboard/create-plan'
						label='Plan'
					/> */}
				</StyledRadioGroup>
				<ButtonsWrapper>
					<Dialog.Close>
						<Button fullWidth variant='secondary' label='Cancel' />
					</Dialog.Close>
					<Button
						label='Create'
						onClick={handleRedirect}
						disabled={value.length === 0}
						fullWidth
					/>
				</ButtonsWrapper>
			</Content>

			<Dialog
				open={isInfoModalOpen}
				onOpenChange={(open) => {
					setIsInfoModalOpen(open)
				}}
			>
				<Content
					className='info-modal'
					overlayStyles={{
						zIndex: 1001
					}}
					contentPositionStyles={{ zIndex: 1001 }}
				>
					<Title
						variant='h2'
						weight='semi-bold'
						style={{ textAlign: 'center' }}
					>
						Heads Up!
					</Title>
					<StyledTypography weight='medium' style={{ textAlign: 'center' }}>
						{currentUser?.freeTrail ? (
							currentUser?.role?.name === 'Owner' ? (
								<>
									You&apos;re about to add your 11th client. FortunAI&apos;s
									free trial includes tax planning for up to 10 clients. By
									adding more,
									<StyledSpan> your 14-day free trial will end</StyledSpan>, and
									your
									<StyledSpan>
										{' '}
										paid subscription will begin immediately.
									</StyledSpan>{' '}
									Would you like to proceed?
								</>
							) : (
								'During your free trial, you can only create 10 clients. To create more clients, please contact your organization owner to upgrade the subscription plan.'
							)
						) : currentUser?.role?.name === 'Owner' ? (
							'Your organization has reached the maximum number of clients allowed on the current plan.'
						) : (
							'Your organization has reached the maximum number of clients allowed on the current plan. Please contact your organization owner to upgrade the subscription plan.'
						)}
					</StyledTypography>

					{currentUser?.freeTrail && currentUser?.role?.name === 'Owner' ? (
						<ButtonsColumn>
							<Button
								variant='primary'
								label='Yes, Start My Subscription'
								fullWidth
								disabled={isLoading}
								isLoading={isLoading}
								onClick={handleSkipActiveTrial}
							/>
							<Button
								variant='secondary'
								label='Cancel'
								fullWidth
								onClick={() => {
									setIsInfoModalOpen(false)
								}}
							/>
						</ButtonsColumn>
					) : (
						<Button
							variant='primary'
							label='Ok'
							fullWidth
							onClick={() => {
								setIsInfoModalOpen(false)
							}}
						/>
					)}
				</Content>
			</Dialog>

			<ChoosePlanModal
				title={
					isOnFreePlan(currentUser)
						? 'Upgrade to Add More Clients'
						: `Subscribe to a Pricing Plan to Add ${
								value === '/client-management/create-client'
									? 'Clients'
									: value === '/client-management/create-company'
									? 'Companies'
									: 'Plans'
						  }`
				}
				description={
					isOnFreePlan(currentUser)
						? getFreePlanClientLimitMessage(currentUser)
						: 'You currently do not have an active subscription. Please subscribe to a pricing plan to activate your 14-day free trial.'
				}
				isOpen={isChoosePlanModalOpen}
				setIsOpen={setIsChoosePlanModalOpen}
			/>

			<EnterpriseModal
				isOpen={isEnterpriseModalOpen}
				onOpenChange={setIsEnterpriseModalOpen}
				skip='/client-management/create-client'
				setOpenCreate={setOpen}
			/>
		</Dialog>
	)
}
